import { GatedQueryError } from "../components/GatedQueryError";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  bulkUpdateVariantLeadTimes,
  listVariantPolicies,
  upsertVariantPolicy,
} from "../api/reorder_policies";
import type { StockyPublicBigIntId } from "../api/stocky_public_ids";
import { getPlanPreview } from "../api/plan_previews";
import { DemandStanceCard } from "../components/DemandStanceCard";
import { LeadTimeSuggestionCard } from "../components/LeadTimeSuggestionCard";
import { ReplenishSuggestionCard } from "../components/ReplenishSuggestionCard";
import { ReorderPointExplanation } from "../components/ReorderPointExplanation";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { ReorderCsvImportModal } from "../components/ReorderCsvImportModal";
import { VariantSupplierModal } from "../components/VariantSupplierModal";
import type { VariantPolicyRow } from "../types";
import { printBarcodeLabels } from "../utils/printBarcodeLabels";
import {
  BarcodeLabelResultBanner,
  type BarcodeLabelReport,
} from "../components/BarcodeLabelResultBanner";
import {
  filterPolicyRows,
  formatDailyDemand,
  groupBySupplier,
  isReorderPointUnset,
  needsReorder,
  parseIntField,
  parsePositiveInt,
  toUpsertValue,
  type SupplierGroup,
} from "../utils/reorderPolicies";

import { t } from "../lib/i18n";
import "./ReorderPolicies.css";

const QUERY_KEY = ["variant-policies"];
/**
 * 1つの仕入先カードで一度に描く行数の上限。
 * 🔴 1行あたり6つの入力欄がある＝上限が無いと、Pro の上限（商品1万件）に近い店で
 * 数万個のフォーム部品を一度に生成して画面が固まる。残りは「さらに表示」で足す
 * （絞り込みと併用すれば、目的の商品には検索で先に届く）。
 */
const ROWS_PER_GROUP_STEP = 50;
/** 日販を平均する窓。backend の `forecast.DEFAULT_WINDOW_DAYS` と揃える。 */
const DEMAND_WINDOW_DAYS = 30;

export function ReorderPolicies() {
  return (
    <PlanDataPreviewGate
      feature="reorder"
      featureName={t("発注点（リードタイム）設定")}
      previewQueryKey={["plan-preview", "reorder-policies"]}
      previewQueryFn={async () => [await getPlanPreview("reorder_policies")]}
      sections={[
        {
          dataset: "reorder_policies",
          title: t("発注点を設定できる商品"),
          columns: [
            { key: "product", label: t("商品") },
            { key: "sku", label: "SKU" },
            { key: "supplier", label: t("仕入先") },
            { key: "available", label: t("在庫") },
            { key: "incoming", label: t("入荷予定") },
            { key: "ordered_open", label: t("未入荷発注数") },
            { key: "reorder_point", label: t("発注点") },
            { key: "lead_time", label: t("リードタイム") },
          ],
        },
      ]}
    >
      <ReorderPoliciesContent />
    </PlanDataPreviewGate>
  );
}

function ReorderPoliciesContent() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [importOpen, setImportOpen] = useState(false);
  const policiesQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: listVariantPolicies,
  });

  const upsert = useMutation({
    mutationFn: (args: {
      shopifyVariantId: StockyPublicBigIntId;
      reorderPoint?: number;
      leadTimeDays?: number | null;
      daysOfSupplyTarget?: number | null;
      serviceLevelPct?: number | null;
      minOrderQty?: number | null;
      replenishDays?: number | null;
    }) =>
      upsertVariantPolicy(args.shopifyVariantId, {
        // 0＝手動固定を解除して自動計算へ戻す（列が non-nullable のため null は送らない）
        reorder_point: args.reorderPoint,
        lead_time_days: args.leadTimeDays,
        days_of_supply_target: args.daysOfSupplyTarget,
        service_level_pct: args.serviceLevelPct,
        min_order_qty: args.minOrderQty,
        replenish_days: args.replenishDays,
      }),
    // commit 後の応答喪失でもサーバーの状態へ収束させる。
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const bulkApply = useMutation({
    mutationFn: (args: { group: SupplierGroup; leadTimeDays: number }) =>
      bulkUpdateVariantLeadTimes(
        args.group.rows.map((row) => row.shopify_variant_id),
        args.leadTimeDays,
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  // 絞り込み。全件がすでに手元にあるので、サーバーを増やさず手元で絞る。
  const [searchInput, setSearchInput] = useState("");
  const [unsetOnly, setUnsetOnly] = useState(false);
  const allRows = useMemo(() => policiesQuery.data ?? [], [policiesQuery.data]);
  const visibleRows = useMemo(
    () => filterPolicyRows(allRows, { query: searchInput, unsetOnly }),
    [allRows, searchInput, unsetOnly],
  );
  const unsetCount = useMemo(
    () => allRows.filter(isReorderPointUnset).length,
    [allRows],
  );
  const groups = useMemo(() => groupBySupplier(visibleRows), [visibleRows]);
  const allRowsBySupplier = useMemo(
    () =>
      new Map(groupBySupplier(allRows).map((group) => [group.key, group.rows])),
    [allRows],
  );

  const [labelResult, setLabelResult] = useState<BarcodeLabelReport | null>(
    null,
  );

  const isBusy = upsert.isPending || bulkApply.isPending;

  // 棚札用のバーコードラベル。発注書と違い数量の根拠が無いので1商品1枚
  // （数えるための枚数ではなく「棚に貼る札」＝再印刷もここから）。
  // 🔑 対象は「表示中の商品」＝絞り込みの結果。ボタンの説明文がそう言っている以上、
  //    全件を出すと嘘になる（検索して10件に絞ったのに1万枚出る）。
  const handlePrintBarcodeLabels = () => {
    setLabelResult(
      printBarcodeLabels(
        visibleRows.map((row) => ({
          sku: row.sku,
          barcode: row.barcode,
          title: row.title,
          qty: 1,
        })),
      ),
    );
  };

  return (
    <Page
      title={t("発注点（リードタイム）設定")}
      subtitle={t(
        "仕入先ごとにリードタイム（発注〜入荷までの日数）と安全在庫日数を入力すると、販売速度から発注点を自動計算します",
      )}
      secondaryActions={[
        {
          content: t("StockyのCSVから取り込む"),
          onAction: () => setImportOpen(true),
        },
        {
          content: t("バーコードラベル（印刷）"),
          onAction: handlePrintBarcodeLabels,
          // 🔴 枚数を先に見せる＝絞り込まずに押すと全商品ぶん出る。押してから
          //    「1万枚だった」と気づく作りにしない。
          helpText: t(
            "絞り込み中の {n} 件の棚札を1枚ずつ出します（A4・3列）。バーコード未設定の商品は出ません",
            { n: visibleRows.length },
          ),
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {/* 🔴 数字が変わる場所に置く＝別の設定画面に隠すと、発注点を見て
                  いる最中に「これは何の値か」を確かめに行けない。 */}
            <DemandStanceCard />
            <BarcodeLabelResultBanner
              result={labelResult}
              onDismiss={() => setLabelResult(null)}
            />
            <ReorderCsvImportModal
              open={importOpen}
              onClose={() => setImportOpen(false)}
              onImported={() =>
                queryClient.invalidateQueries({ queryKey: QUERY_KEY })
              }
            />
            {policiesQuery.isError && (
              <GatedQueryError
                title={t("商品の取得に失敗しました")}
                onRetry={() => void policiesQuery.refetch()}
              />
            )}

            {upsert.isError && (
              <Banner
                tone="critical"
                title={t("保存結果を確認できませんでした")}
              >
                <BlockStack gap="100">
                  <Text as="p">{(upsert.error as Error).message}</Text>
                  <Text as="p">
                    {t(
                      "通信状況によっては保存済みの可能性があります。最新の状態を再取得しています。表示を確認してから、必要な場合だけもう一度保存してください。",
                    )}
                  </Text>
                </BlockStack>
              </Banner>
            )}

            {bulkApply.isPending && bulkApply.variables && (
              <Banner tone="info" title={t("リードタイムを一括適用しています")}>
                <p>
                  {t(
                    "{supplier} の {count} 件をまとめて更新しています。完了するまで、この画面の保存操作はできません。",
                    {
                      supplier:
                        bulkApply.variables.group.supplierName ??
                        t("仕入先 未割当"),
                      count: bulkApply.variables.group.rows.length,
                    },
                  )}
                </p>
              </Banner>
            )}

            {bulkApply.isError && bulkApply.variables && (
              <Banner
                tone="critical"
                title={t("リードタイムの一括適用結果を確認できませんでした")}
              >
                <BlockStack gap="100">
                  <Text as="p">{(bulkApply.error as Error).message}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t(
                      "通信状況によっては適用済みの可能性があります。最新の状態を再取得しています。対象: {items}",
                      {
                        items: bulkTargetSummary(
                          bulkApply.variables.group.rows,
                        ),
                      },
                    )}
                  </Text>
                </BlockStack>
              </Banner>
            )}

            {bulkApply.isSuccess && bulkApply.variables && (
              <Banner
                tone="success"
                title={t("リードタイムを一括適用しました")}
                onDismiss={() => bulkApply.reset()}
              >
                <p>
                  {t("{supplier} の {count} 件をすべて更新しました。", {
                    supplier:
                      bulkApply.variables.group.supplierName ??
                      t("仕入先 未割当"),
                    count: bulkApply.data.updated,
                  })}
                </p>
              </Banner>
            )}

            <LeadTimeSuggestionCard
              disabled={isBusy}
              onApplied={() =>
                void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
              }
            />

            <ReplenishSuggestionCard
              disabled={isBusy}
              onApplied={() =>
                void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
              }
            />

            {policiesQuery.isLoading && (
              <Card>
                <SkeletonBodyText lines={8} />
              </Card>
            )}

            {!policiesQuery.isLoading &&
              !policiesQuery.isError &&
              allRows.length === 0 && (
                // 空状態には必ず行き先を添える（bug-hunt H群）＝本文が挙げる
                // 商品カタログはShopify同期だけが作るため、同期を唯一の導線にする。
                <Banner
                  tone="info"
                  title={t("商品がまだ同期されていません")}
                  action={{
                    content: t("同期する"),
                    onAction: () => navigate("/dashboard"),
                  }}
                >
                  <p>
                    {t(
                      "在庫同期を実行すると、ここに商品が並び、リードタイムを設定できます。",
                    )}
                  </p>
                </Banner>
              )}

            {!policiesQuery.isLoading && allRows.length > 0 && (
              <Card>
                <BlockStack gap="300">
                  <TextField
                    label={t("商品を検索")}
                    value={searchInput}
                    onChange={setSearchInput}
                    placeholder={t("商品名 / SKU")}
                    autoComplete="off"
                    clearButton
                    onClearButtonClick={() => setSearchInput("")}
                  />
                  <InlineStack gap="300" blockAlign="center">
                    <Checkbox
                      label={t("発注点が未設定の商品だけ（{n}件）", {
                        n: unsetCount,
                      })}
                      checked={unsetOnly}
                      onChange={setUnsetOnly}
                    />
                    <Text as="span" tone="subdued">
                      {t("{shown} / {total} 件を表示中", {
                        shown: visibleRows.length,
                        total: allRows.length,
                      })}
                    </Text>
                  </InlineStack>
                  {/* 🔴 「未設定＝在庫が足りている」ではない＝アプリが発注要否を
                        判断できていない。この画面が初日の崖そのものなので明示する。 */}
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t(
                      "発注点が未設定の商品は、発注が必要かどうかをアプリが判断できず、注文書の自動提案にも並びません。リードタイムと在庫日数の目標を入れると自動計算が始まります。",
                    )}
                  </Text>
                </BlockStack>
              </Card>
            )}

            {allRows.length > 0 && groups.length === 0 && (
              <Banner
                tone="info"
                title={t("条件に一致する商品はありません")}
                action={{
                  content: t("絞り込みを解除"),
                  onAction: () => {
                    setSearchInput("");
                    setUnsetOnly(false);
                  },
                }}
              >
                <p>
                  {unsetOnly && searchInput.trim() === ""
                    ? t(
                        "発注点が未設定の商品はありません（すべての商品で自動計算が有効です）。",
                      )
                    : t("商品名・SKU の一部で探せます。")}
                </p>
              </Banner>
            )}

            {groups.map((group) => (
              <SupplierGroupCard
                key={group.key}
                group={group}
                bulkRows={allRowsBySupplier.get(group.key) ?? group.rows}
                disabled={isBusy}
                bulkLoading={
                  bulkApply.isPending &&
                  bulkApply.variables?.group.key === group.key
                }
                onSaveRow={(row, edits) =>
                  upsert.mutate({
                    shopifyVariantId: row.shopify_variant_id,
                    ...edits,
                  })
                }
                onBulkApply={(rows, leadTimeDays) =>
                  bulkApply
                    .mutateAsync({
                      group: { ...group, rows },
                      leadTimeDays,
                    })
                    .then(() => undefined)
                }
              />
            ))}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

/** 1行ぶんの編集内容。位置引数を増やし続けないためのまとめ型。 */
interface VariantPolicyEdits {
  /** 手動固定の発注点。0＝解除（自動計算へ戻す）。 */
  reorderPoint?: number;
  leadTimeDays?: number | null;
  daysOfSupplyTarget?: number | null;
  serviceLevelPct?: number | null;
  minOrderQty?: number | null;
  replenishDays?: number | null;
}

interface SupplierGroupCardProps {
  group: SupplierGroup;
  bulkRows: VariantPolicyRow[];
  disabled: boolean;
  bulkLoading: boolean;
  onSaveRow: (row: VariantPolicyRow, edits: VariantPolicyEdits) => void;
  onBulkApply: (
    rows: VariantPolicyRow[],
    leadTimeDays: number,
  ) => Promise<void>;
}

function SupplierGroupCard({
  group,
  bulkRows,
  disabled,
  bulkLoading,
  onSaveRow,
  onBulkApply,
}: SupplierGroupCardProps) {
  const [bulkLeadTime, setBulkLeadTime] = useState("");
  const [shownCount, setShownCount] = useState(ROWS_PER_GROUP_STEP);

  // 絞り込みで行が入れ替わったら表示数も初期化する（前の検索で広げた分を持ち越さない）。
  useEffect(() => {
    setShownCount(ROWS_PER_GROUP_STEP);
  }, [group.rows.length]);

  const shownRows = group.rows.slice(0, shownCount);
  const hiddenCount = group.rows.length - shownRows.length;

  const handleBulkApply = async () => {
    const parsed = parsePositiveInt(bulkLeadTime);
    if (parsed === undefined || parsed < 1) return;
    // 🔴 一括適用は「見えている行」ではなく **この仕入先の全行** に効く（表示上限は
    //    描画の都合であって対象の定義ではない）。件数を出して取り違えを防ぐ。
    try {
      await onBulkApply(bulkRows, parsed);
      setBulkLeadTime("");
    } catch {
      // 親の mutation banner が失敗を表示する。入力は再試行できるよう保持する。
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="050">
            <Text as="h2" variant="headingMd">
              {group.supplierName ?? t("仕入先 未割当")}
            </Text>
            {/* 一括適用が効くのは「見えている行」ではなくこの仕入先の全行＝件数を
                見出しの下に出して、表示上限との取り違えを防ぐ。 */}
            <Text as="span" variant="bodySm" tone="subdued">
              {t("{n} 件（一括適用はこの仕入先の全商品に効きます）", {
                n: bulkRows.length,
              })}
            </Text>
          </BlockStack>
          <InlineStack gap="200" blockAlign="end">
            <Box width="160px">
              <TextField
                label={t("この仕入先に一括適用（日）")}
                labelHidden
                type="number"
                autoComplete="off"
                placeholder={t("リードタイム日数")}
                value={bulkLeadTime}
                onChange={setBulkLeadTime}
                disabled={disabled}
                min="1"
              />
            </Box>
            <Button
              onClick={() => void handleBulkApply()}
              disabled={disabled || (parsePositiveInt(bulkLeadTime) ?? 0) < 1}
              loading={bulkLoading}
            >
              {t("一括適用")}
            </Button>
          </InlineStack>
        </InlineStack>

        <BlockStack gap="300">
          {shownRows.map((row) => (
            <VariantRowEditor
              key={row.shopify_variant_id}
              row={row}
              disabled={disabled}
              onSave={onSaveRow}
            />
          ))}
        </BlockStack>

        {hiddenCount > 0 && (
          <InlineStack align="center">
            <Button
              onClick={() => setShownCount((n) => n + ROWS_PER_GROUP_STEP)}
            >
              {t("さらに表示（残り {n} 件）", { n: hiddenCount })}
            </Button>
          </InlineStack>
        )}
      </BlockStack>
    </Card>
  );
}

function bulkTargetSummary(rows: VariantPolicyRow[]): string {
  const visible = rows
    .slice(0, 5)
    .map((row) => row.sku ?? row.title ?? String(row.shopify_variant_id));
  const remaining = rows.length - visible.length;
  return remaining > 0
    ? t("{items}、ほか {count} 件", {
        items: visible.join("、"),
        count: remaining,
      })
    : visible.join("、");
}

/** 🔴 モジュール直下で t() を呼ぶと initLocale より前に評価され locale が効かない。
 *  描画のたびに引き直すため関数にする。 */
function serviceLevelOptions() {
  // 🔑 「サービスレベル」「90%」は統計語＝マーチャントには何が起きるか分からない
  //    （ユーザー指摘 2026-08-04）。何が増えるかを言葉で書く。%は括弧内に残す
  //    ＝既存店が自分の設定を見失わないように（値そのものは変えていない）。
  return [
    { label: t("標準（90%）"), value: "90" },
    { label: t("厚め（95%）"), value: "95" },
    { label: t("かなり厚め（99%）"), value: "99" },
  ];
}
const DEFAULT_SERVICE_LEVEL = "90";

interface VariantRowEditorProps {
  row: VariantPolicyRow;
  disabled: boolean;
  onSave: (row: VariantPolicyRow, edits: VariantPolicyEdits) => void;
}

function VariantRowEditor({ row, disabled, onSave }: VariantRowEditorProps) {
  const [leadTime, setLeadTime] = useState(
    row.lead_time_days === null ? "" : String(row.lead_time_days),
  );
  const [daysOfSupply, setDaysOfSupply] = useState(
    row.days_of_supply_target === null ? "" : String(row.days_of_supply_target),
  );
  const [serviceLevel, setServiceLevel] = useState(
    row.service_level_pct === null
      ? DEFAULT_SERVICE_LEVEL
      : String(row.service_level_pct),
  );
  const [minOrderQty, setMinOrderQty] = useState(
    row.min_order_qty === null ? "" : String(row.min_order_qty),
  );
  const [replenishDays, setReplenishDays] = useState(
    row.replenish_days === null ? "" : String(row.replenish_days),
  );
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  // 発注点の手動固定。0 は「自動計算」の意味なので空欄で表示する。
  // 🔴 この欄が無かった間、季節性の画面が「発注点設定で発注点を空にすると自動計算に
  // なります」と案内していたのに、その操作がどこにも存在しなかった（CSV 取込で
  // 入れた手動発注点を画面から外せない＝案内が行き止まりだった）。
  const [reorderPoint, setReorderPoint] = useState(
    row.reorder_point !== null && row.reorder_point > 0
      ? String(row.reorder_point)
      : "",
  );
  // 🔴 発注点の手動固定は「6つ目の入力欄」ではない＝残り5つ（リードタイム・安全在庫・
  // 厚さ・補充日数・MOQ）を全部無効にする上書き。横に同格で並べると、そのことが
  // 画面のどこにも出ないまま「とりあえず埋める」欄に見える。押したときだけ出す。
  // ⚠️ 既に固定値が入っている行は最初から開く（隠すと自分が設定した値が消えたように
  // 見え、しかもその値が効き続ける＝いちばん質の悪い黙り方になる）。
  const [overrideOpen, setOverrideOpen] = useState(
    row.reorder_point !== null && row.reorder_point > 0,
  );

  // サーバ値が変わったら表示を追随させる（一括適用・refetch 後の反映）。
  // useState は初期化きりで props の変化に追随しないため、これが無いと
  // 「一括適用→保存成功→画面は古いまま＝効いていないように見える」
  // （2026-07-18 実機バグ）。値が変わったフィールドだけ上書きするので、
  // 無関係な入力中の編集は保持される。
  useEffect(() => {
    setLeadTime(row.lead_time_days === null ? "" : String(row.lead_time_days));
  }, [row.lead_time_days]);
  useEffect(() => {
    setDaysOfSupply(
      row.days_of_supply_target === null
        ? ""
        : String(row.days_of_supply_target),
    );
  }, [row.days_of_supply_target]);
  useEffect(() => {
    setServiceLevel(
      row.service_level_pct === null
        ? DEFAULT_SERVICE_LEVEL
        : String(row.service_level_pct),
    );
  }, [row.service_level_pct]);
  useEffect(() => {
    setMinOrderQty(row.min_order_qty === null ? "" : String(row.min_order_qty));
  }, [row.min_order_qty]);
  useEffect(() => {
    setReplenishDays(
      row.replenish_days === null ? "" : String(row.replenish_days),
    );
  }, [row.replenish_days]);
  useEffect(() => {
    setReorderPoint(
      row.reorder_point !== null && row.reorder_point > 0
        ? String(row.reorder_point)
        : "",
    );
  }, [row.reorder_point]);
  // 🔴 開く方向にだけ効かせる（片道）＝CSV 取込や一括操作でサーバー側に手動固定が
  // 入ったら必ず見える。閉じる方向にも効かせると、編集中に refetch が走った瞬間に
  // 入力欄が畳まれて打ちかけの値が見えなくなる。
  useEffect(() => {
    if (row.reorder_point !== null && row.reorder_point > 0) {
      setOverrideOpen(true);
    }
  }, [row.reorder_point]);

  const isBelowReorderPoint = needsReorder(row);
  /** 同じ文言のボタンを行ごとに一意にするための呼び名（読み上げ・E2E の行特定用）。 */
  const rowLabel = row.title ?? row.sku ?? t("この商品");

  const leadTimeParsed = parseIntField(leadTime);
  const daysOfSupplyParsed = parseIntField(daysOfSupply);
  const minOrderQtyParsed = parseIntField(minOrderQty, 1);
  const replenishDaysParsed = parseIntField(replenishDays);
  const reorderPointParsed = parseIntField(reorderPoint, 1);
  const reorderPointError =
    reorderPointParsed.kind === "invalid" ? t("1以上の整数で入力") : undefined;
  const isManualReorderPoint = reorderPointParsed.kind === "value";
  // 🔴 自動計算がいくつになるのかを入力欄で言う（ユーザー指摘 2026-08-04）。
  // 内訳は実装と一致させる＝統計的安全在庫が乗っている行では必ずその項も出す
  // （出さないと「日販×日数」の式が表示値と合わず、説明のほうが嘘になる）。
  const autoSafetyUnits = Math.round(row.safety_stock_units);
  const autoCoverDays =
    (row.lead_time_days ?? 0) + (row.days_of_supply_target ?? 0);
  const autoReorderPointHelp =
    autoSafetyUnits > 0
      ? t(
          "空欄＝販売速度から自動計算。いまは {point}（日販 {demand} × {days}日分 ＋ 統計的安全在庫 {safety}）。",
          {
            point: row.effective_reorder_point,
            demand: formatDailyDemand(row.avg_daily_demand),
            days: autoCoverDays,
            safety: autoSafetyUnits,
          },
        )
      : t(
          "空欄＝販売速度から自動計算。いまは {point}（日販 {demand} × {days}日分）。",
          {
            point: row.effective_reorder_point,
            demand: formatDailyDemand(row.avg_daily_demand),
            days: autoCoverDays,
          },
        );
  // 上書きを閉じているあいだの1行。🔴 内訳をここでも出す＝畳んだ瞬間に
  // 「発注点がどこから来た数字か」が画面から消えるなら、畳む価値が無い
  // （安全在庫が乗る行では必ずその項も出す＝出さないと式が表示値と合わない）。
  const collapsedReorderPointSummary = isManualReorderPoint
    ? t("{point} で固定中（自動計算・季節係数は効きません）", {
        point: reorderPointParsed.value,
      })
    : row.effective_reorder_point > 0
      ? autoSafetyUnits > 0
        ? t(
            "自動計算で {point}（日販 {demand} × {days}日分 ＋ 統計的安全在庫 {safety}）",
            {
              point: row.effective_reorder_point,
              demand: formatDailyDemand(row.avg_daily_demand),
              days: autoCoverDays,
              safety: autoSafetyUnits,
            },
          )
        : t("自動計算で {point}（日販 {demand} × {days}日分）", {
            point: row.effective_reorder_point,
            demand: formatDailyDemand(row.avg_daily_demand),
            days: autoCoverDays,
          })
      : t("リードタイムと安全在庫日数を入れると自動計算が始まります");
  const leadTimeError =
    leadTimeParsed.kind === "invalid" ? t("0以上の整数で入力") : undefined;
  const daysOfSupplyError =
    daysOfSupplyParsed.kind === "invalid" ? t("0以上の整数で入力") : undefined;
  const minOrderQtyError =
    minOrderQtyParsed.kind === "invalid" ? t("1以上の整数で入力") : undefined;
  const replenishDaysError =
    replenishDaysParsed.kind === "invalid" ? t("0以上の整数で入力") : undefined;
  const hasError = Boolean(
    leadTimeError ||
    daysOfSupplyError ||
    minOrderQtyError ||
    replenishDaysError ||
    reorderPointError,
  );

  const handleSave = () => {
    if (hasError) return;
    // Empty field → null (clear); a value → that number. Both sent explicitly
    // so a blanked-out field clears the policy value. Service level always
    // sends a concrete value (the selector never has an empty state).
    onSave(row, {
      // 発注点は列が non-nullable（0＝自動計算）なので、空欄は null ではなく 0 を送る。
      reorderPoint:
        reorderPointParsed.kind === "value" ? reorderPointParsed.value : 0,
      leadTimeDays: toUpsertValue(leadTimeParsed),
      daysOfSupplyTarget: toUpsertValue(daysOfSupplyParsed),
      serviceLevelPct: Number(serviceLevel),
      minOrderQty: toUpsertValue(minOrderQtyParsed),
      replenishDays: toUpsertValue(replenishDaysParsed),
    });
  };

  return (
    <Box
      padding="300"
      borderColor="border"
      borderWidth="025"
      borderRadius="200"
    >
      <BlockStack gap="300">
        <BlockStack gap="050">
          <Text as="span" variant="bodyMd" fontWeight="semibold">
            {row.title ?? t("（商品名なし）")}
          </Text>
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodySm" tone="subdued">
              {row.sku ?? t("SKUなし")}
            </Text>
            <Text as="span" variant="bodySm" tone="subdued">
              {/* 「在庫」は全拠点合算＝注文書ドラフト（発注先の主拠点のみ）と
                  数字が違い得るので、範囲を明記する（C3・503 vs 6 を実測）。 */}
              {t("在庫（全拠点合算） {stock} ・ 日販 {demand}", {
                stock: row.available,
                demand: formatDailyDemand(row.avg_daily_demand),
              })}
            </Text>
            {isBelowReorderPoint ? (
              <Badge tone="critical">
                {t("要発注（発注点 {point}）", {
                  point: row.effective_reorder_point,
                })}
              </Badge>
            ) : row.effective_reorder_point > 0 ? (
              <Badge tone="success">
                {t("発注点 {point}", { point: row.effective_reorder_point })}
              </Badge>
            ) : (
              <Badge tone="attention">{t("未設定")}</Badge>
            )}
            {row.replenishment_target > row.effective_reorder_point && (
              <Text as="span" variant="bodySm" tone="subdued">
                {t("補充目標 {target} まで発注", {
                  target: row.replenishment_target,
                })}
              </Text>
            )}
            {row.safety_stock_units > 0 && (
              <Text as="span" variant="bodySm" tone="subdued">
                {t("うち統計的安全在庫 +{n}", {
                  n: Math.round(row.safety_stock_units),
                })}
              </Text>
            )}
            {/* 手法が既定の移動平均から外れた行にだけ注記を出す（全行に並ぶと
                ノイズになる）。同じ履歴から違う日販が出る理由を画面で追えるように。 */}
            {row.demand_method === "sba" && (
              <Text as="span" variant="bodySm" tone="subdued">
                {t("たまにしか売れない商品として計算")}
              </Text>
            )}
            {/* 🔴 欠品していた日は「売れなかった」であって「需要が無かった」では
                ない。黙っていると日販が低い理由が画面のどこにも無く、発注点も
                低いままになる（欠品しがちな商品ほど発注が減る自己強化ループ）。
                ⚠️ 観測は同期の間隔ごと＝「欠品だった日」と言い切らない。 */}
            {(row.stockout_days ?? 0) > 0 && (
              <Text as="span" variant="bodySm" tone="caution">
                {t(
                  "直近{window}日のうち{days}日は欠品あり（日販はその分低く出ています）",
                  {
                    window: DEMAND_WINDOW_DAYS,
                    days: row.stockout_days ?? 0,
                  },
                )}
              </Text>
            )}
            {/* 🔴 断片が最大6個並ぶ＝マーチャントが自分で組み立てる必要がある。
                押したときだけ1段落にまとめる（常時生成は費用が発散する）。 */}
            <ReorderPointExplanation
              shopifyVariantId={row.shopify_variant_id}
            />
          </InlineStack>
        </BlockStack>

        <div
          className={`ReorderPolicyFields${overrideOpen ? " ReorderPolicyFields--withOverride" : ""}`}
        >
          <div className="ReorderPolicyField">
            <TextField
              label={t("リードタイム（日）")}
              type="number"
              autoComplete="off"
              // 🔴 この数字を入れたのがマーチャントか我々かを言う。実績から自動で
              // 入れた値を無印で置くと、自分が入力したものだと思われる（そして
              // 「知らないうちに発注点が立っていた」になる）。書き換えれば消える。
              helpText={
                row.lead_time_source === "measured"
                  ? t("発注〜入荷の実績から自動で入れました")
                  : undefined
              }
              value={leadTime}
              onChange={setLeadTime}
              disabled={disabled}
              error={leadTimeError}
              min="0"
            />
          </div>
          <div className="ReorderPolicyField">
            <TextField
              label={t("安全在庫（日）")}
              type="number"
              autoComplete="off"
              value={daysOfSupply}
              onChange={setDaysOfSupply}
              disabled={disabled}
              error={daysOfSupplyError}
              min="0"
            />
          </div>
          <div className="ReorderPolicyField">
            <Select
              label={t("安全在庫の厚さ")}
              helpText={t(
                "売れ行きのばらつきに、どれだけ備えるか。厚くすると発注量が増えます。",
              )}
              options={serviceLevelOptions()}
              value={serviceLevel}
              onChange={setServiceLevel}
              disabled={disabled}
            />
          </div>
          <div className="ReorderPolicyField">
            <TextField
              label={t("補充日数")}
              type="number"
              autoComplete="off"
              placeholder={t("発注点まで")}
              helpText={t("1回の発注で何日分まで補充するか")}
              value={replenishDays}
              onChange={setReplenishDays}
              disabled={disabled}
              error={replenishDaysError}
              min="0"
            />
          </div>
          <div className="ReorderPolicyField">
            <TextField
              label={t("最低発注数量（MOQ）")}
              type="number"
              autoComplete="off"
              placeholder={t("未設定")}
              value={minOrderQty}
              onChange={setMinOrderQty}
              disabled={disabled}
              error={minOrderQtyError}
              min="1"
            />
          </div>
          {/* 🔴 ここから先は「上の5つを全部無効にする」操作＝同格の入力欄として
              並べない。上書きを開いたときだけ6つ目の欄として同じ基準線に置く。 */}
          {overrideOpen ? (
            <div className="ReorderPolicyField">
              <TextField
                label={t("発注点（手動）")}
                type="number"
                autoComplete="off"
                // 🔴 「自動計算」とだけ書くと、いくつになるのかが画面のどこにも
                // 出ていない＝マーチャントは自分が何に同意したのか分からないまま
                // 発注書が出てくる（ユーザー指摘 2026-08-04）。実際に効いている数を
                // 入力欄そのものに出す。数値はサーバーが返す effective_reorder_point
                // ＝式をこちらで持たない（3面で答えが割れるのを避ける）。
                placeholder={
                  row.effective_reorder_point > 0
                    ? t("自動計算 {point}", {
                        point: row.effective_reorder_point,
                      })
                    : t("自動計算")
                }
                // 🔴 手動固定はリードタイム・安全在庫・サービスレベル・季節係数を
                // すべて上書きする＝そのことをこの場で言う（黙って無効化しない）。
                helpText={
                  isManualReorderPoint
                    ? t(
                        "この値で固定中（自動計算・季節係数は効きません）。空欄なら自動計算で {point} になります。",
                        {
                          point: row.effective_reorder_point,
                        },
                      )
                    : row.effective_reorder_point > 0
                      ? autoReorderPointHelp
                      : t(
                          "空欄＝販売速度から自動計算。リードタイムと安全在庫日数を入れると数値が決まります。",
                        )
                }
                value={reorderPoint}
                onChange={setReorderPoint}
                disabled={disabled}
                error={reorderPointError}
                min="1"
              />
            </div>
          ) : null}
        </div>
        {!overrideOpen ? (
          <Text as="span" variant="bodySm" tone="subdued">
            {collapsedReorderPointSummary}
          </Text>
        ) : null}
        <div className="ReorderPolicyActions">
          <InlineStack gap="200" blockAlign="center" align="end" wrap>
            <Button
              variant="plain"
              disclosure={overrideOpen ? "up" : "down"}
              onClick={() => setOverrideOpen((open) => !open)}
              disabled={disabled}
              // 同じ文言のボタンが行数ぶん並ぶ＝読み上げでは区別できない。
              accessibilityLabel={`${rowLabel} の発注点を上書きする`}
            >
              {t("発注点を上書きする")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={disabled || hasError}
            >
              {t("保存")}
            </Button>
            {/* 相見積もり（Stocky parity v2 Step 6）。ここに置くのは、上の MOQ・
              リードタイムが「仕入先を1社しか使わない店の設定」で、複数社を使う店は同じ項目を社ごとに持つから＝同じ場所で切り替えられるのが素直。 Pro 未満はモーダル内の API が 403 を返すので、開くこと自体は許す
              （何が使えないのかを見せないと、上げる理由が伝わらない）。 */}
            <Button
              onClick={() => setSupplierModalOpen(true)}
              disabled={disabled}
              // 同じ文言のボタンが行数ぶん並ぶ＝読み上げでは全部同じに聞こえる。
              // 商品名を含めて一意にする（E2E がこの名前で行を特定するのも兼ねる）。
              accessibilityLabel={`${rowLabel} の仕入先別の条件`}
            >
              {t("仕入先別の条件")}
            </Button>
          </InlineStack>
        </div>
      </BlockStack>
      {supplierModalOpen ? (
        <VariantSupplierModal
          open
          onClose={() => setSupplierModalOpen(false)}
          variantId={row.variant_id}
          title={row.title ?? row.sku ?? t("（商品名なし）")}
        />
      ) : null}
    </Box>
  );
}
